import * as config from '@/config/config.json';
import * as calculatorConfig from '@/config/base-config.json';
import { generateSliderSteps } from '@/utils/generateSliderSteps';

export const PDF_FILE_NAME = 'OTHERS_DECOUPLING_PART_SHARE_PURCHASE';

export const EXISTING_SHARE_NOTIFICATION = {
  TITLE: 'Invalid Existing Share %',
  DESCRIPTION: 'Existing Share of Joint & Exiting Owners must amount to 100%.',
};

export const FINAL_SHARE_NOTIFICATION = {
  TITLE: 'Invalid Final Share %',
  DESCRIPTION:
    'Final Share of Joint Owner must be equal or greater than the initial Existing Share ',
};

export const OTHERS_PART_SHARE_LABEL = {
  JointOwnershipDetails: 'Joint Ownership Details',
  StayingOwners: 'Staying Owners',
  StayingOwner: 'Staying Owner',
  ExitingOwner: 'Exiting Owner',
  ExitingOwners: 'Exiting Owners',
  PartSharePurchaseTimeline: 'Part Share Purchase Timeline',
  Proceed: 'Proceed',
  GotIt: 'Got it',
  PropertyName: 'Property Name / Address',
  PropertyValuation: 'Property Valuation',
  ExistingLoan: 'Existing Loan',
  NumberOfJointOwners: 'Number of Joint Owners',
  NumberOfExitingOwners: 'Number of Exiting Owners',
  Name: 'Name',
  ExistingShare: 'Existing Share',
  Residency: 'Residency',
  HoldingOfProperties: 'Holding of Properties',
  FinalShare: 'Final Share',
  BuyingOverExitingOwnerPropertyValue: "Buying Over Exiting Owner's Property Value",
  Downpayment: 'Downpayment (Cash)',
  DownpaymentReceivable: 'Downpayment Receivable',
  BalanceDownpayment: 'Balance Downpayment (Cash/CPF)',
  PartPurchaseLoan: 'Part Purchase Loan',
  BSD: "Buyer's Stamp Duty (BSD)",
  ABSD: "Additional Buyer's Stamp Duty",
  ABSDShortForm: 'ABSD',
  BSDShortForm: 'BSD',
  LegalFee: 'Legal Fee',
  Others: 'Others',
  TotalPartPurchaseOutlay: 'Total Part Purchase Outlay',
  TotalDownpayment: 'Total Downpayment',
  Expenses: 'Expenses',
  NewRestructuredLoan: 'New Restructured Loan',
  LoanTenure: 'Loan Tenure (Years)',
  Interest: 'Interest',
  MonthlyNewRestructuredLoanInstalments: 'Monthly New Restructured Loan Instalments',
  Total: 'Total',
  CPFUsedWithAccruedInterest: 'CPF Used With Accrued Interest',
  TotalExpenses: 'Total Expenses',
  SSD: "Seller's Stamp Duty",
  SSDPayable: 'SSD Payable',
  BankPenalty: "Bank's Penalty",
  OutstandingLoan: 'Outstanding Loan',
  SalesProceeds: 'Sales Proceeds',
  OTP: 'Option to Purchase (OTP)',
  ExercisePeriod: 'Exercise Period',
  CompletionPeriod: 'Completion Period',
  Timeline: 'Timeline',
  ExerciseOfOTP: 'Exercise of OTP',
  SalesCompletion: 'Sales Completion',
  PotentialExpenses: 'Potential Expenses',
  Payables: 'Payables',
  OutstandingPayablesInCPFOACash: 'Outstanding Payables in CPF OA / Cash',
  GenerateTimeline: 'Generate Timeline',
  Weeks: 'Weeks',
};

export const TOTAL_EXISTING_SHARE = 100;
export const TOTAL_FINAL_SHARE = 100;
export const EXISTING_SHARE_1 = 33;
export const EXISTING_SHARE_2 = 33;
export const FINAL_SHARE_1 = 50;
export const CHART_COLORS = ['#F25A73', '#9475EB'];

const minDownpayment =
  config.min_cash_downpayment_in_percent.private_residential.first_loan.upto_65_financing_year;
const maxLTV = config.max_ltv_in_percent.private_residential.first_loan.upto_65_financing_year;

export const DOWNPAYMENT = {
  MIN: minDownpayment,
  MAX: 99,
  DEFAULT: minDownpayment,
};

export const LOAN_TO_VALUE = {
  MIN: 0,
  MAX: maxLTV,
  DEFAULT: maxLTV,
};

export const BALANCE_DOWNPAYMENT = 100 - (minDownpayment + maxLTV);

export enum JointOwners {
  TWO = '2',
  THREE = '3',
}

export enum ExitingOwners {
  ONE = '1',
  TWO = '2',
}

export enum Residence {
  CITIZEN = 'citizen',
  PR = 'pr',
  FOREIGNER = 'foreigner',
  ENTITY_TRUST = 'entityOrTrust',
}

export enum HoldingProperties {
  FIRST = '1st',
  SECOND = '2nd',
  THIRD = '3rd',
}

export const getResidencyText = (key: string) => {
  switch (key) {
    case Residence.CITIZEN: {
      return 'SG Citizen';
    }
    case Residence.PR: {
      return 'PR';
    }
    case Residence.FOREIGNER: {
      return 'Foreigner';
    }
    case Residence.ENTITY_TRUST: {
      return 'Entity / Trust';
    }

    default: {
      return '';
    }
  }
};

export const JOINT_OWNERS_LIST = [
  { value: JointOwners.TWO, label: JointOwners.TWO },
  { value: JointOwners.THREE, label: JointOwners.THREE },
];

export const EXITING_OWNERS_LIST = [
  { value: ExitingOwners.ONE, label: ExitingOwners.ONE },
  { value: ExitingOwners.TWO, label: ExitingOwners.TWO },
];

export const EXISTING_SHARE = {
  MIN: 1,
  MAX: 99,
  STEP: 1,
  LABEL_INPUTS: [1, 99],
  PLACEHOLDER: '0',
};

export const FINAL_SHARE = {
  MIN: 1,
  MAX: 99,
  STEP: 1,
  LABEL_INPUTS: [1, 99],
  PLACEHOLDER: '0',
};

const maxLoanTenure = Math.max(
  config.max_loan_tenure_in_years.hdb.bank_loan.upto_65_financing_year,
  config.max_loan_tenure_in_years.hdb.bank_loan.upto_75_financing_year,
  config.max_loan_tenure_in_years.hdb.hdb_loan,
  config.max_loan_tenure_in_years.ec.upto_65_financing_year,
  config.max_loan_tenure_in_years.ec.upto_75_financing_year,
  config.max_loan_tenure_in_years.commercial_industrial,
  config.max_loan_tenure_in_years.private_residential.upto_65_financing_year,
  config.max_loan_tenure_in_years.private_residential.upto_75_financing_year,
);

export const LOAN_TENURE = {
  MIN: 1,
  MAX: maxLoanTenure,
  STEP: 1,
  LABEL_INPUTS: generateSliderSteps(1, maxLoanTenure, 10),
  PLACEHOLDER: '0',
};

export const RESIDENCY_LIST = Object.values(Residence).map((residency) => {
  return {
    value: residency,
    label: getResidencyText(residency),
  };
});

export const HOLDING_PROPERTIES_LIST = Object.values(HoldingProperties).map((property) => {
  return {
    value: property,
    label: property,
  };
});

export enum TimelineName {
  TIMELINE_1 = 'Timeline 1',
  TIMELINE_2 = 'Timeline 2',
}

export const TIMELINE_NAMES = [TimelineName.TIMELINE_1, TimelineName.TIMELINE_2];

export const getShareTypeName = (numberOfJointOwners: string, numberOfExitingOwners: string) => {
  if (numberOfJointOwners === JointOwners.THREE && numberOfExitingOwners === ExitingOwners.ONE) {
    return '3_to_2';
  }

  if (numberOfJointOwners === JointOwners.THREE && numberOfExitingOwners === ExitingOwners.TWO) {
    return '3_to_1';
  }

  return '2_to_1';
};

export const EXISTING_OWNER_PAYMENT = {
  cpfAccruedInterestUsed: '',
  ssd: '0',
  bankPenalty: '',
  legalFee: calculatorConfig.legal_fee.toString(),
  others: '',
};

//two joint owners and one exiting owners
export const THREE_TO_TWO = {
  jointOwners: [
    {
      existingShare: EXISTING_SHARE_1,
      name: '',
      residency: Residence.CITIZEN,
      numberOfHoldingProperties: HoldingProperties.FIRST,
      finalShare: FINAL_SHARE_1,
    },
    {
      name: '',
      existingShare: EXISTING_SHARE_2,
      residency: Residence.CITIZEN,
      numberOfHoldingProperties: HoldingProperties.FIRST,
      finalShare: TOTAL_FINAL_SHARE - FINAL_SHARE_1,
    },
  ],
  exitingOwners: [
    { name: '', existingShare: TOTAL_EXISTING_SHARE - (EXISTING_SHARE_1 + EXISTING_SHARE_2) },
  ],
};

//one joint owners and two exiting owners
export const THREE_TO_ONE = {
  jointOwners: [
    {
      existingShare: EXISTING_SHARE_1,
      name: '',
      residency: Residence.CITIZEN,
      numberOfHoldingProperties: HoldingProperties.FIRST,
      finalShare: TOTAL_FINAL_SHARE,
    },
  ],
  exitingOwners: [
    {
      name: '',
      existingShare: EXISTING_SHARE_2,
    },
    {
      name: '',
      existingShare: TOTAL_EXISTING_SHARE - (EXISTING_SHARE_1 + EXISTING_SHARE_2),
    },
  ],
};

// one joint owner and one exiting owner
export const TWO_TO_ONE = {
  jointOwners: [
    {
      existingShare: TOTAL_EXISTING_SHARE / 2,
      name: '',
      residency: Residence.CITIZEN,
      numberOfHoldingProperties: HoldingProperties.FIRST,
      finalShare: TOTAL_FINAL_SHARE,
    },
  ],
  exitingOwners: [
    {
      name: '',
      existingShare: TOTAL_EXISTING_SHARE / 2,
    },
  ],
};

export const EXERCISE_PERIOD = {
  DEFAULT: calculatorConfig.exercise_period_in_weeks.default,
  MIN: calculatorConfig.exercise_period_in_weeks.min,
  MAX: calculatorConfig.exercise_period_in_weeks.max,
  STEP: 1,
  LABEL_INPUTS: generateSliderSteps(
    calculatorConfig.exercise_period_in_weeks.min,
    calculatorConfig.exercise_period_in_weeks.max,
  ),
};

export const COMPLETION_PERIOD = {
  DEFAULT: calculatorConfig.completion_period_in_weeks.default,
  MIN: calculatorConfig.completion_period_in_weeks.min,
  MAX: calculatorConfig.completion_period_in_weeks.max,
  STEP: 1,
  LABEL_INPUTS: generateSliderSteps(
    calculatorConfig.completion_period_in_weeks.min,
    calculatorConfig.completion_period_in_weeks.max,
  ),
};
